import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { RootState } from './store';
import APIService from '../Utils/APIService';
import { getUserType } from './auth.store';
import { URLConfig } from '../Utils/features';
import { DEFAULT_PAGE_SIZE } from '../Utils/constants';
import { fetchRoutes } from './trips.store';
// import { OperationSummary } from './analytics.store';

export type LocationSummary = { cancelled: number; delivered: number; planned: number; skipped: number };
export type RouteSummary = { completed: number; in_progress: number; planned: number; unplanned: number };

export interface TripActionsState {
  summaryLoading: boolean;
  summary: { locations?: LocationSummary; routes?: RouteSummary };
}

const initialState: TripActionsState = {
  summaryLoading: false,
  summary: {},
};

export const sendSMS = createAsyncThunk<any, { tripId: string }>(
  'routes/send-sms',
  async ({ tripId }, { rejectWithValue }) => {
    const [err, data] = await to(APIService.post(`user/trip/${tripId}/driver-sms`));
    if (err) {
      toast.error('Unable to send the SMS. Please try again');
      return rejectWithValue(err);
    }
    toast.success('SMS sent successfully!');
    return data?.data;
  },
);

export const updateRouteName = createAsyncThunk<
  any,
  {
    name: string;
    tripId: string;
    driverId: number;
    vehicleId: number;
    departureDateTime?: string;
    arrivalDateTime?: string;
  }
>(
  'routes/update-name',
  async ({ name, tripId, driverId, vehicleId, departureDateTime, arrivalDateTime }, { rejectWithValue }) => {
    const [err] = await to(
      APIService.put(`user/trip/${tripId}`, {
        tripName: name,
        driver_id: driverId,
        vehicle_id: vehicleId,
        departure: departureDateTime,
        arrival: arrivalDateTime,
      }),
    );
    if (err) {
      toast.error('Unable to update trip name. Please try again');
      return rejectWithValue(err);
    }
    toast.success('Update the name successfully!');
    return { name, tripId, driverId, vehicleId, departureDateTime, arrivalDateTime };
  },
);

export const copyOperations = createAsyncThunk<any, { tripId: string }>(
  'route/copy-operation',
  async ({ tripId }, { rejectWithValue }) => {
    const [err, data] = await to(APIService.get(`user/summary-locations?tripID=${tripId}`));
    if (err) {
      toast.error('Unable to fetch the operation details');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const fetchOperationSummary = createAsyncThunk<
  any,
  { licenseNo: string; startDate: string; endDate: string; tripName: string }
>('operations/summary', async ({ startDate, endDate, licenseNo, tripName }, { rejectWithValue, getState }) => {
  const userType = getUserType(getState() as RootState);
  const url = URLConfig.getOperationSummary[userType];
  if (!url) return rejectWithValue('Not available for this user type');
  const [err, data] = await to(
    APIService.get(url, {
      params: { startDate, endDate, tripName, licenseNo },
    }),
  );
  if (err) {
    toast.error('Fetching Operation Summary Failed. Please try again');
    return rejectWithValue(err);
  }
  return data?.data;
});

export const saveLocationComment = createAsyncThunk<any, { tripId: number; locationId: number; comment: string }>(
  'tripActions/save-comment',
  async ({ tripId, locationId, comment }, { rejectWithValue }) => {
    const [err] = await to(
      APIService.put(`user/trip/${tripId}/location/${locationId}/comments`, {
        comments: comment,
      }),
    );
    if (err) {
      toast.error('Failed to save comment. Please try again');
      return rejectWithValue(err);
    }
    toast.success('Comment saved');
    return { tripId, locationId, comment };
  },
);

export const completeTrip = createAsyncThunk<any, { tripId: number; remark?: string; actualDistance?: number }>(
  'routes/complete-trip',
  async ({ tripId, remark, actualDistance }, { rejectWithValue, dispatch }) => {
    const [err, data] = await to(
      APIService.put(`user/trip/${tripId}/complete`, {
        remark,
        actualDistance,
      }),
    );
    if (err) {
      toast.error('Could not complete the trip. Please try again!');
      return rejectWithValue(err);
    }
    toast.success('Trip marked as complete. Unvisited locations skipped.');
    dispatch(
      fetchRoutes({ start: 0, limit: DEFAULT_PAGE_SIZE, licenseNo: '', startDate: '', endDate: '', tripName: '' }),
    );
    return data?.data;
  },
);

export const tripActionsSlice = createSlice({
  name: 'tripActions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchOperationSummary.pending, (s) => {
      s.summaryLoading = true;
    });
    builder.addCase(fetchOperationSummary.rejected, (s) => {
      s.summaryLoading = false;
    });
    builder.addCase(fetchOperationSummary.fulfilled, (s, { payload }) => {
      s.summaryLoading = false;
      s.summary = payload;
    });
  },
});

const tripActionsStore = (store: RootState): TripActionsState => store.tripActions;
export const getOperationSummaryLoading = createSelector(tripActionsStore, (s) => s.summaryLoading);
export const getOperationSummary = createSelector(tripActionsStore, (s) => s.summary);

export default tripActionsSlice.reducer;
